'use client'

import React, { Suspense } from 'react'

import { HydrationBoundary, dehydrate } from '@tanstack/react-query'

import { RecipientDetail } from '../RecipientDetail'
import {
  ChartExpandedProvider,
  useChartExpanded,
} from '../RecipientDetail/ChartExpandedContext'
import { DetailFilterProvider, SectionSkeleton } from '../shared'

export interface SuspenseRecipientViewV2Props {
  id: string
  dehydratedState?: ReturnType<typeof dehydrate>
}

/**
 * Enhanced Suspense-enabled Recipient View with compound components.
 *
 * "Federal Data Observatory" dashboard design:
 * - Data-dense but clean layout
 * - Progressive loading with Suspense boundaries
 * - Sticky tab navigation for section jumping
 * - Interactive charts and visualizations
 * - Fiscal year filtering
 * - Deep linking throughout
 *
 * Data is prefetched server-side via getStaticProps and dehydrated into
 * the React Query cache. useSuspenseQuery hooks resolve immediately from
 * the cache during hydration, avoiding suspension and hydration mismatches.
 * Secondary queries (portfolio, outlays, ceilings) load client-side.
 */
export function SuspenseRecipientViewV2({
  id,
  dehydratedState,
}: SuspenseRecipientViewV2Props) {
  return (
    <HydrationBoundary state={dehydratedState}>
      <RecipientDetail.Provider recipientId={id}>
        <DetailFilterProvider>
          <ChartExpandedProvider>
            <RecipientPageContent />
          </ChartExpandedProvider>
        </DetailFilterProvider>
      </RecipientDetail.Provider>
    </HydrationBoundary>
  )
}

/** Reads ChartExpandedContext so the toggle only re-renders this wrapper, not the whole page. */
function CashFlowContainer() {
  const { isChartExpanded } = useChartExpanded()

  return (
    <div
      className={`w-full px-4 pb-6 md:px-8 ${isChartExpanded ? '' : 'mx-auto max-w-7xl'}`}
    >
      <Suspense fallback={<SectionSkeleton height={280} bars={0} />}>
        <RecipientDetail.AwardCashFlow />
      </Suspense>
    </div>
  )
}

function RecipientPageContent() {
  return (
    <div className="flex w-full flex-col items-center pb-8">
      {/* Header with certification badges, identifiers, and KPIs */}
      <RecipientDetail.Header />

      {/* Quick Actions bar */}
      <RecipientDetail.QuickActions />

      {/* Section navigation tabs - sticky below header */}
      <RecipientDetail.Minimap />

      {/* Main content area */}
      <main className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8">
        <div className="space-y-6">
          {/* Quick Insights panel - vertical layout */}
          <RecipientDetail.Insights />
        </div>
      </main>

      {/* Cash flow chart — own container so it can go full-width when expanded */}
      <CashFlowContainer />

      {/* Remaining analytics sections */}
      <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
        <div className="space-y-6">
          <RecipientDetail.Sections className="gap-6">
            {/* Two-column: Award mix and top agencies */}
            <Suspense fallback={<SectionSkeleton height={180} />}>
              <RecipientDetail.AwardMix />
            </Suspense>
            <Suspense fallback={<SectionSkeleton height={220} bars={5} />}>
              <RecipientDetail.TopAgencies />
            </Suspense>

            {/* Industry codes - data table style */}
            <Suspense fallback={<SectionSkeleton height={280} bars={5} />}>
              <RecipientDetail.Industry />
            </Suspense>

            {/* Geography map - full width */}
            <Suspense fallback={<SectionSkeleton height={280} bars={0} />}>
              <RecipientDetail.Geography />
            </Suspense>

            {/* Two-column: Set-asides and competition */}
            <Suspense fallback={<SectionSkeleton height={180} bars={4} />}>
              <RecipientDetail.SetAsides />
            </Suspense>
            <Suspense fallback={<SectionSkeleton height={180} bars={4} />}>
              <RecipientDetail.Competition />
            </Suspense>

            {/* Contract sizes */}
            <Suspense fallback={<SectionSkeleton height={200} bars={0} />}>
              <RecipientDetail.ContractSizes />
            </Suspense>

            {/* Executive officers (full-width) */}
            <Suspense fallback={<SectionSkeleton height={100} bars={2} />}>
              <RecipientDetail.Officers />
            </Suspense>
          </RecipientDetail.Sections>

          {/* Recent awards table */}
          <RecipientDetail.RecentAwards />
        </div>
      </div>
    </div>
  )
}
